import {
  invitePlatformStaffRequestSchema,
  PLATFORM_ROLES,
  platformRoleSchema,
  type IssuedInvitation,
  type PlatformRole,
  type PlatformStaffMember,
} from '@avrahami/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type SyntheticEvent } from 'react';
import { keys, platformApi, staffQuery } from '../api/platform';
import { sessionQuery } from '../api/session';
import { he } from '../i18n/he';
import { errorMessage } from '../lib/format';
import {
  Alert,
  Badge,
  Button,
  Card,
  Cell,
  ConfirmDialog,
  CopyField,
  formatDateTime,
  invitationLink,
  Loading,
  PageHeader,
  SelectField,
  Table,
  TextField,
} from '@avrahami/ui';

/** Platform owners manage the platform staff here. */
export function StaffPage() {
  const staff = useQuery(staffQuery);
  const session = useQuery(sessionQuery);
  const selfId = session.data?.kind === 'signed-in' ? session.data.me.user.id : undefined;
  const [issued, setIssued] = useState<{ email: string; invitation: IssuedInvitation | null }>();

  return (
    <>
      <PageHeader title={he.staff.title} />
      <div className="flex flex-col gap-6">
        <InviteForm
          onInvited={(email, invitation) => {
            setIssued({ email, invitation });
          }}
        />
        {issued === undefined ? null : issued.invitation === null ? (
          <Alert tone="info">{he.staff.existingAccount}</Alert>
        ) : (
          <Card title={`${he.staff.inviteTitle}: ${issued.email}`}>
            <div className="flex flex-col gap-2">
              <p className="text-sm text-slate-600">{he.staff.inviteBody}</p>
              <CopyField
                label={he.staff.inviteTitle}
                value={invitationLink(window.location.origin, issued.invitation.token)}
                testId="invitation-link"
              />
            </div>
          </Card>
        )}
        <Card>
          {staff.isPending ? <Loading /> : null}
          {staff.isError ? <Alert>{errorMessage(staff.error)}</Alert> : null}
          {staff.data === undefined ? null : (
            <Table
              head={[
                he.staff.name,
                he.staff.email,
                he.staff.role,
                he.staff.status,
                he.staff.mfa,
                '',
              ]}
            >
              {staff.data.items.map((member) => (
                <StaffRow
                  key={member.userId}
                  member={member}
                  isSelf={member.userId === selfId}
                  onInvitation={(invitation) => {
                    setIssued({ email: member.email, invitation });
                  }}
                />
              ))}
            </Table>
          )}
        </Card>
      </div>
    </>
  );
}

function InviteForm({
  onInvited,
}: {
  onInvited: (email: string, invitation: IssuedInvitation | null) => void;
}) {
  const queryClient = useQueryClient();
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [role, setRole] = useState<PlatformRole>('PLATFORM_SUPPORT');
  const [invalid, setInvalid] = useState(false);
  const invite = useMutation({
    mutationFn: platformApi.inviteStaff,
    onSuccess: async (result) => {
      onInvited(result.member.email, result.invitation);
      setEmail('');
      setDisplayName('');
      await queryClient.invalidateQueries({ queryKey: keys.staff });
    },
  });
  const onSubmit = (event: SyntheticEvent) => {
    event.preventDefault();
    const parsed = invitePlatformStaffRequestSchema.safeParse({ email, displayName, role });
    setInvalid(!parsed.success);
    if (parsed.success) invite.mutate(parsed.data);
  };
  return (
    <Card title={he.staff.invite}>
      <form
        onSubmit={onSubmit}
        className="flex flex-wrap items-end gap-3"
        noValidate
        autoComplete="off"
      >
        <TextField
          label={he.staff.email}
          name="email"
          type="email"
          autoComplete="off"
          className="ltr w-64"
          value={email}
          onChange={(event) => {
            setEmail(event.target.value);
          }}
        />
        <TextField
          label={he.staff.name}
          name="displayName"
          autoComplete="off"
          className="w-48"
          value={displayName}
          onChange={(event) => {
            setDisplayName(event.target.value);
          }}
        />
        <SelectField
          label={he.staff.role}
          name="role"
          value={role}
          onChange={(event) => {
            setRole(platformRoleSchema.parse(event.target.value));
          }}
        >
          {PLATFORM_ROLES.map((option) => (
            <option key={option} value={option}>
              {he.roles[option]}
            </option>
          ))}
        </SelectField>
        <Button type="submit" busy={invite.isPending}>
          {he.staff.submit}
        </Button>
      </form>
      {invalid ? (
        <div className="mt-3">
          <Alert>{he.errors.VALIDATION_FAILED}</Alert>
        </div>
      ) : null}
      {invite.isError ? (
        <div className="mt-3">
          <Alert>{errorMessage(invite.error)}</Alert>
        </div>
      ) : null}
    </Card>
  );
}

function StaffRow({
  member,
  isSelf,
  onInvitation,
}: {
  member: PlatformStaffMember;
  isSelf: boolean;
  onInvitation: (invitation: IssuedInvitation) => void;
}) {
  const queryClient = useQueryClient();
  const [confirmRemove, setConfirmRemove] = useState(false);
  const refresh = () => queryClient.invalidateQueries({ queryKey: keys.staff });
  const changeRole = useMutation({
    mutationFn: (role: PlatformRole) => platformApi.changeStaffRole(member.userId, role),
    onSuccess: refresh,
  });
  const remove = useMutation({
    mutationFn: () => platformApi.removeStaff(member.userId),
    onSuccess: async () => {
      setConfirmRemove(false);
      await refresh();
    },
  });
  const reissue = useMutation({
    mutationFn: () => platformApi.reissueStaffInvitation(member.userId),
    onSuccess: async (invitation) => {
      onInvitation(invitation);
      await refresh();
    },
  });
  const error = changeRole.error ?? reissue.error;

  return (
    <tr data-testid="staff-row" data-email={member.email}>
      <Cell>
        {member.displayName}{' '}
        {isSelf ? <span className="text-xs text-slate-500">{he.staff.you}</span> : null}
      </Cell>
      <Cell className="ltr">{member.email}</Cell>
      <Cell>
        {isSelf ? (
          he.roles[member.role]
        ) : (
          <select
            aria-label={he.staff.role}
            value={member.role}
            disabled={changeRole.isPending}
            onChange={(event) => {
              changeRole.mutate(platformRoleSchema.parse(event.target.value));
            }}
            className="rounded-md border border-slate-300 bg-white px-2 py-1 text-sm"
          >
            {PLATFORM_ROLES.map((option) => (
              <option key={option} value={option}>
                {he.roles[option]}
              </option>
            ))}
          </select>
        )}
      </Cell>
      <Cell>
        {member.needsPasswordSetup ? (
          <div className="flex flex-col items-start gap-1">
            <Badge tone="amber">{he.tenant.needsPassword}</Badge>
            {member.invitationExpiresAt === null ? null : (
              <span className="text-xs text-slate-500">
                {he.tenant.invitationUntil} {formatDateTime(member.invitationExpiresAt)}
              </span>
            )}
            <Button
              tone="secondary"
              busy={reissue.isPending}
              onClick={() => {
                reissue.mutate();
              }}
            >
              {he.staff.newInvitation}
            </Button>
          </div>
        ) : (
          <Badge tone="green">{he.tenant.passwordSet}</Badge>
        )}
      </Cell>
      <Cell>
        {member.mfaEnabled ? (
          <Badge tone="green">{he.staff.mfaOn}</Badge>
        ) : (
          <Badge tone="slate">{he.staff.mfaOff}</Badge>
        )}
      </Cell>
      <Cell>
        {isSelf ? null : (
          <Button
            tone="secondary"
            onClick={() => {
              remove.reset();
              setConfirmRemove(true);
            }}
          >
            {he.staff.remove}
          </Button>
        )}
        {error === null ? null : <Alert>{errorMessage(error)}</Alert>}
        <ConfirmDialog
          open={confirmRemove}
          title={he.staff.removeTitle}
          confirmLabel={he.staff.remove}
          tone="danger"
          busy={remove.isPending}
          onConfirm={() => {
            remove.mutate();
          }}
          onClose={() => {
            setConfirmRemove(false);
          }}
        >
          <p>
            {member.displayName} (<span className="ltr">{member.email}</span>)
          </p>
          <p className="mt-2">{he.staff.removeBody}</p>
          {remove.isError ? <Alert>{errorMessage(remove.error)}</Alert> : null}
        </ConfirmDialog>
      </Cell>
    </tr>
  );
}
